// Photo URL resolver. Ported from mobile/lib/photo.ts. Web is same-origin,
// so the Wikimedia proxy base is the relative '/img/wm/'. Wikimedia
// rate-limits direct hotlinks → route through the Worker proxy. Google
// Places refs must never be persisted (re-fetch per render via the proxy,
// per Google ToS). Other CDNs (Unsplash, Yelp, hotel CDNs) pass through.
//
// Attribution is required on every rendered image (CC-BY/CC-BY-SA +
// Unsplash/Pexels) — `credit` carries the source for the caption.

const WM_RE = /^https?:\/\/upload\.wikimedia\.org\/wikipedia\/commons\//;

export interface ResolvedPhoto {
  src: string;
  credit?: string;
}

type PhotoInput =
  | string
  | { url?: string; ref?: string; source?: string }
  | null
  | undefined;

export function resolvePhoto(p: PhotoInput): ResolvedPhoto | null {
  if (!p) return null;
  if (typeof p === 'string') {
    if (!p.startsWith('http')) return null;
    return {
      src: p.replace(WM_RE, '/img/wm/'),
      credit: WM_RE.test(p) ? 'Wikimedia Commons' : undefined,
    };
  }
  if (p.url && p.url.startsWith('http')) {
    return {
      src: p.url.replace(WM_RE, '/img/wm/'),
      credit: p.source || (WM_RE.test(p.url) ? 'Wikimedia Commons' : undefined),
    };
  }
  if (p.ref && p.source === 'google_places') {
    return {
      src: `/api/places/photo?ref=${encodeURIComponent(p.ref)}&w=800`,
      credit: 'Google',
    };
  }
  return null;
}

// Card-tier thumb: append the wm proxy's ?w= param (rounded up
// server-side to Commons' allowed thumb buckets, original served if the
// rewrite fails). Other hosts pass through untouched — external CDNs
// don't accept the param and the Places proxy already carries its own w.
// Detail galleries / lightboxes keep the original by not calling this.
export function thumbSrc(src: string, width: number): string {
  if (!src.startsWith('/img/wm/') || src.includes('?')) return src;
  return `${src}?w=${Math.max(1, Math.round(width))}`;
}

export function resolvePhotos(list: unknown): ResolvedPhoto[] {
  if (!Array.isArray(list)) return [];
  const out: ResolvedPhoto[] = [];
  for (const p of list) {
    const r = resolvePhoto(p as PhotoInput);
    if (r) out.push(r);
  }
  return out;
}
